import { useEffect } from 'react';

export const useVerifoneCheckout = ({
  cardholderSelection,
  isDifferentCardholderConfirmed,
  hospitalId,
  cardholderForm,
  patientForm,
  setCheckoutRendered,
  setPaymentStatus,
  setLoading,
  setError,
  cardAttempts,
  setCardAttempts,
  setCheckoutId,
  setCheckoutBilling,
}) => {
  useEffect(() => {
    const messageHandler = event => {
      if (
        event.origin !== 'https://cst.checkout.vficloud.net' &&
        event.origin !== 'https://prod-emea.checkout.verifone.cloud'
      )
        return;
      if (typeof event.data === 'string' && event.data.startsWith('[iFrameSizer')) return;

      const { data } = event;

      if (data?.event === 'TRANSACTION_SUCCESS') {
        setError('');
        setCardAttempts(0);
        setPaymentStatus('successful');

        const container = document.getElementById('cardholder__verifone-iframe');
        if (container) {
          container.style.display = 'none';
        }
      } else if (data?.event === 'TRANSACTION_FAILED') {
        setCardAttempts(prev => {
          const newAttempts = prev + 1;
          if (newAttempts >= 3) {
            const container = document.getElementById('cardholder__verifone-iframe');
            if (container) {
              container.style.display = 'none';
            }
          }
          return newAttempts;
        });
        setPaymentStatus('unsuccessful');
      } else if (data?.event === 'CHECKOUT_RENDERED') {
        setCheckoutRendered(true);
      }

      console.log(data);
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, [setError, setCardAttempts, setCheckoutRendered, setPaymentStatus]);

  useEffect(() => {
    setCardAttempts(0);
    setError('');
  }, [cardholderSelection, setCardAttempts, setError]);

  useEffect(() => {
    const cleanupIframe = () => {
      const container = document.getElementById('cardholder__verifone-iframe');
      if (container) {
        while (container.firstChild) {
          try {
            container.removeChild(container.firstChild);
          } catch (error) {
            console.warn('Could not remove child:', error);
          }
        }
      }
    };

    cleanupIframe();

    if (cardholderSelection === null) {
      return;
    }
    if (cardholderSelection === 'different' && !isDifferentCardholderConfirmed) {
      return;
    }

    function createCardConfig(id) {
      if (process.env.REACT_APP_ENVIRONMENT_NAME === 'prod') {
        const paymentContractId = JSON.parse(process.env.REACT_APP_VERIFONE_PPC_ID_PROD);
        const card = paymentContractId.find(obj => obj.hospital_id === id);
        if (!card) {
          console.error(`No payment config found for hospital ID: ${id}`);
          return null;
        }

        const orgIds = JSON.parse(process.env.REACT_APP_VERIFONE_ORG_IDS_PROD);
        const orgConfig = orgIds.find(obj => obj.hospital_id === id);
        if (!orgConfig) {
          console.error(`No org/3DS config found for hospital ID: ${id}`);
          return null;
        }

        return {
          AMEX: card.amex_id,
          VISA: card.visa_mastercard_id,
          MASTERCARD: card.visa_mastercard_id,
          organization_id: orgConfig.organization_id,
          secure3d_contract_id: orgConfig.secure3d_contract_id,
        };
      } else {
        const paymentContractId = JSON.parse(process.env.REACT_APP_VERIFONE_PPC_ID_SIMULATOR);
        const orgConfig = JSON.parse(process.env.REACT_APP_VERIFONE_ORG_IDS_SIMULATOR);
        return {
          AMEX: paymentContractId.amex_id,
          VISA: paymentContractId.visa_mastercard_id,
          MASTERCARD: paymentContractId.visa_mastercard_id,
          organization_id: orgConfig.organization_id,
          secure3d_contract_id: orgConfig.secure3d_contract_id,
        };
      }
    }

    async function createCheckout() {
      const cardConfig = createCardConfig(hospitalId);

      if (!cardConfig) {
        console.error('Cannot create checkout: invalid card config');
        return null;
      }

      const url = `${process.env.REACT_APP_APIM_BASE_URL.slice(0, -'booking/'.length)}checkout/consultant/v2/checkout/`;

      const useSameAddress =
        cardholderSelection === 'same' ||
        (cardholderSelection === 'different' && cardholderForm.billing_address.value === 'same');

      const billingAddress = useSameAddress
        ? patientForm.addresslookup
        : cardholderForm.cardholder_addresslookup.value;

      const cardholderDetails =
        cardholderSelection === 'same'
          ? {
              first_name: patientForm.first_name,
              last_name: patientForm.last_name,
              email: patientForm.email,
            }
          : {
              first_name: cardholderForm.cardholder_first_name.value,
              last_name: cardholderForm.cardholder_last_name.value,
              email: cardholderForm.cardholder_email.value,
            };

      const billing = {
        address_1: billingAddress?.line_one || '',
        city: billingAddress?.city || '',
        country_code: 'GB',
        first_name: cardholderDetails.first_name,
        last_name: cardholderDetails.last_name,
        phone: patientForm.phone_number,
        postal_code: billingAddress?.postal_code || '',
      };

      if (billingAddress?.line_two?.trim()) {
        billing.address_2 = billingAddress.line_two;
      }

      const themeId =
        process.env.REACT_APP_ENVIRONMENT_NAME === 'prod'
          ? '3e101d55-af36-4b43-a7b9-446339a2b4a7'
          : '1ce4b565-df15-4ee2-b4cd-9ff10d8beb21';

      const { organization_id, secure3d_contract_id, ...paymentContractIds } = cardConfig;

      const payload = {
        configurations: {
          card: {
            payment_contract_id: paymentContractIds,
            ...(secure3d_contract_id && {
              threed_secure: {
                threeds_contract_id: secure3d_contract_id,
              },
            }),
          },
        },
        customer_details: {
          billing,
          email_address: cardholderDetails.email,
        },
        ...(organization_id && { entity_id: organization_id }),
        theme_id: themeId,
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Ocp-Apim-Subscription-Key': process.env.REACT_APP_APIM_MAPS_SUBSCRIPTION_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const res = await response.text();
          console.error('API Error:', res);
          return null;
        }

        return await response.json();
      } catch (error) {
        console.error('Failed to create checkout:', error);
      }
    }

    setLoading(true);

    createCheckout()
      .then(result => {
        if (result?.id) {
          setCheckoutId(result.id);
        }
        if (result?.url) {
          const useSameAddress =
            cardholderSelection === 'same' ||
            (cardholderSelection === 'different' &&
              cardholderForm.billing_address.value === 'same');

          const billingAddress = useSameAddress
            ? patientForm.addresslookup
            : cardholderForm.cardholder_addresslookup.value;

          const cardholderDetails =
            cardholderSelection === 'same'
              ? {
                  first_name: patientForm.first_name,
                  last_name: patientForm.last_name,
                }
              : {
                  first_name: cardholderForm.cardholder_first_name.value,
                  last_name: cardholderForm.cardholder_last_name.value,
                };

          setCheckoutBilling({
            first_name: cardholderDetails.first_name,
            last_name: cardholderDetails.last_name,
            postcode: billingAddress?.postal_code || '',
            country_code: 'GB',
            address_1: billingAddress?.line_one || '',
            city: billingAddress?.city || '',
          });

          cleanupIframe();

          const script = document.createElement('script');
          script.src = result.url;
          script.async = true;

          const container = document.getElementById('cardholder__verifone-iframe');
          if (container) {
            container.appendChild(script);
          }
        }
      })
      .catch(error => {
        console.error('Checkout error:', error);
      })
      .finally(() => {
        setLoading(false);
      });

    return cleanupIframe;
  }, [
    cardholderSelection,
    hospitalId,
    isDifferentCardholderConfirmed,
    setLoading,
    cardholderForm,
    patientForm,
  ]);
};
